'use client';

import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { Box, Tab, Tabs } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { SearchBox, StyledContainer } from '~/shared';
import { ViewMode } from '~/types';

import { KeywordsTab } from './KeywordsTab';
import { ProductsTab } from './ProductsTab';

export function KeywordPerformanceAnalysis() {
  const [viewMode, setViewMode] = useState<ViewMode>('keywords');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');

  useDebounce(() => setDebouncedQuery(searchQuery), 500, [searchQuery]);

  useEffect(() => {
    setStartDateError('');
    setEndDateError('');

    if (startDate && Number.isNaN(startDate.getTime())) {
      setStartDateError(' ');
      return;
    }

    if (endDate && Number.isNaN(endDate.getTime())) {
      setEndDateError(' ');
      return;
    }

    if (startDate && endDate && endDate < startDate) {
      setEndDateError(' ');
    }
  }, [startDate, endDate]);

  const handleViewMode = useCallback(
    (_: React.SyntheticEvent, next: ViewMode) => {
      setViewMode(next);
      setSearchQuery('');
    },
    []
  );

  const searchLabel =
    viewMode === 'keywords'
      ? 'Buscar palavra chave'
      : 'Buscar produto patrocinado';

  return (
    <StyledContainer>
      <Tabs
        value={viewMode}
        onChange={handleViewMode}
        sx={{
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 40,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 40,
            gap: 0.75,
            px: 2,
          },
        }}
      >
        <Tab value="keywords" label="Palavras-chave" />
        <Tab value="products" label="Produtos" />
      </Tabs>

      <Box
        sx={theme => ({
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        })}
      >
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <SearchBox
            label={searchLabel}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </Box>

        <DatePicker
          label="Início"
          value={startDate}
          onChange={v => setStartDate(v)}
          slotProps={{
            textField: {
              error: !!startDateError,
              variant: 'filled',
            },
            actionBar: { actions: ['clear'] },
          }}
        />

        <DatePicker
          label="Término"
          value={endDate}
          onChange={v => setEndDate(v)}
          slotProps={{
            textField: {
              error: !!endDateError,
              variant: 'filled',
            },
            actionBar: { actions: ['clear'] },
          }}
        />
      </Box>

      {viewMode === 'keywords' ? (
        <KeywordsTab
          debouncedQuery={debouncedQuery}
          startDate={startDate}
          endDate={endDate}
        />
      ) : (
        <ProductsTab
          debouncedQuery={debouncedQuery}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </StyledContainer>
  );
}
